import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  Link,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Building2, UserPlus, LogIn, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

const RegisterForm = ({ onSubmit, loading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [localError, setLocalError] = useState('');

  const validate = () => {
    if (!email.trim()) { setLocalError('Sähköposti on pakollinen'); return false; }
    if (!EMAIL_RE.test(email)) { setLocalError('Syötä kelvollinen sähköpostiosoite'); return false; }
    if (password.length < 6) { setLocalError('Salasanan tulee olla vähintään 6 merkkiä'); return false; }
    if (password !== confirmPassword) { setLocalError('Salasanat eivät täsmää'); return false; }
    if (!acceptedTerms) { setLocalError('Sinun tulee hyväksyä käyttöehdot'); return false; }
    setLocalError('');
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onSubmit({ email: email.trim(), password });
  };

  const displayError = localError || error;

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {displayError && <Alert severity="error">{displayError}</Alert>}

        <TextField
          fullWidth label="Sähköposti" type="email"
          value={email} onChange={(e) => setEmail(e.target.value)} required
        />
        <TextField
          fullWidth label="Salasana" type="password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          required helperText="Vähintään 6 merkkiä"
        />
        <TextField
          fullWidth label="Vahvista salasana" type="password"
          value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
        />
        <FormControlLabel
          control={
            <Checkbox checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)} color="primary" />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Hyväksyn{' '}
              <Link href="/terms" target="_blank" rel="noopener" underline="hover" color="primary">
                käyttöehdot
              </Link>
            </Typography>
          }
        />
        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
          {loading ? 'Luodaan tiliä...' : 'Luo tili ja liity'}
        </Button>
      </Stack>
    </form>
  );
};

const LoginForm = ({ onSubmit, loading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setLocalError('Täytä kaikki kentät'); return; }
    setLocalError('');
    onSubmit({ email: email.trim(), password });
  };

  const displayError = localError || error;

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {displayError && <Alert severity="error">{displayError}</Alert>}

        <TextField
          fullWidth label="Sähköposti" type="email"
          value={email} onChange={(e) => setEmail(e.target.value)} required
        />
        <TextField
          fullWidth label="Salasana" type="password"
          value={password} onChange={(e) => setPassword(e.target.value)} required
        />
        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}>
          {loading ? 'Kirjaudutaan...' : 'Kirjaudu ja liity'}
        </Button>
      </Stack>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const JoinOrganization = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { user, register, login } = useAuth();
  const { isDark } = useTheme();

  const organizationId = searchParams.get('organization');
  const from = location.state?.from || { pathname: '/profiili' };

  const [tab, setTab] = useState(0); // 0 = register, 1 = login
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [orgError, setOrgError] = useState('');

  // ------------------------------------------------------------------
  // Fetch org info
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!organizationId) {
      setOrgError('Kutsulinkkiä ei löydy. Pyydä uusi linkki organisaation ylläpitäjältä.');
      return;
    }
    const fetchOrg = async () => {
      setLoadingOrg(true);
      try {
        const infoDoc = await getDoc(doc(db, 'organizations', organizationId, 'public', 'info'));
        if (infoDoc.exists()) {
          setOrganizationInfo({ id: organizationId, ...infoDoc.data() });
        } else {
          setOrgError('Virheellinen organisaatiokutsulinkki');
        }
      } catch {
        setOrgError('Organisaatiotietojen lataaminen epäonnistui');
      } finally {
        setLoadingOrg(false);
      }
    };
    fetchOrg();
  }, [organizationId]);

  // ------------------------------------------------------------------
  // Join helper – adds org to an already-authenticated user's profile
  // ------------------------------------------------------------------
  const joinOrganization = async (uid) => {
    // Adjust this to match your actual Firestore data model
    await updateDoc(doc(db, 'users', uid), {
      organizationId,
      organizationName: organizationInfo?.name || '',
      organizations: arrayUnion(organizationId),
    });
  };

  // ------------------------------------------------------------------
  // Already-logged-in: just join
  // ------------------------------------------------------------------
  const handleJoinAsExistingUser = async () => {
    setError('');
    setLoading(true);
    try {
      await joinOrganization(user.uid);
      setJoined(true);
    } catch (err) {
      setError(err.message || 'Liittyminen epäonnistui. Yritä uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // New user: register then join
  // ------------------------------------------------------------------
  const handleRegister = async ({ email, password }) => {
    setError('');
    setLoading(true);
    try {
      const userData = {
        email,
        organizationId,
        organizationName: organizationInfo?.name || '',
      };
      const result = await register(email, password, userData);
      if (result.success) {
        setJoined(true);
      } else {
        setError(result.error || 'Tilin luominen epäonnistui');
      }
    } catch (err) {
      setError(err.message || 'Odottamaton virhe. Yritä uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Existing user logging in: login then join
  // ------------------------------------------------------------------
  const handleLogin = async ({ email, password }) => {
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        // result.user.uid or use the updated `user` from context after login
        const uid = result.user?.uid;
        if (uid) await joinOrganization(uid);
        setJoined(true);
      } else {
        setError(result.error || 'Kirjautuminen epäonnistui');
      }
    } catch (err) {
      setError(err.message || 'Odottamaton virhe. Yritä uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Loading org
  // ------------------------------------------------------------------
  if (loadingOrg) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Ladataan...</Typography>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Shared: org banner
  // ------------------------------------------------------------------
  const OrgBanner = () => (
    <Paper
      variant="outlined"
      sx={{
        p: 2, mb: 3,
        bgcolor: 'primary.50',
        borderColor: 'primary.main',
        borderRadius: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Building2 size={16} />
        <Typography variant="subtitle2" fontWeight={600}>Organisaatiokutsu</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">Sinut on kutsuttu liittymään:</Typography>
      <Chip label={organizationInfo?.name} color="primary" size="small" sx={{ mt: 1 }} />
    </Paper>
  );

  // ------------------------------------------------------------------
  // Success state
  // ------------------------------------------------------------------
  if (joined) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', pt: 4, pb: 6 }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckCircle2 size={48} color="var(--mui-palette-success-main, #2e7d32)" />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Liityit onnistuneesti!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Olet nyt jäsen organisaatiossa{' '}
                <strong>{organizationInfo?.name}</strong>.
              </Typography>
              <Button variant="contained" onClick={() => navigate(from, { replace: true })}>
                Jatka
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Org not found
  // ------------------------------------------------------------------
  if (orgError) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', pt: 4, pb: 6 }}>
        <Container maxWidth="sm">
          <Alert severity="error">{orgError}</Alert>
        </Container>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Already logged in → just confirm join
  // ------------------------------------------------------------------
  if (user) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', pt: 4, pb: 6 }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 52, height: 52, borderRadius: '14px', bgcolor: 'primary.main',
                boxShadow: isDark ? '0 0 0 8px rgba(34,211,238,0.08)' : '0 0 0 8px rgba(8,145,178,0.08)',
                mb: 2.5,
              }}
            >
              <Building2 size={26} color="#fff" />
            </Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">Liity organisaatioon</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Kirjautunut: <strong>{user.email}</strong>
            </Typography>
          </Box>

          <Card>
            <CardContent sx={{ p: 4 }}>
              <OrgBanner />

              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Olet jo kirjautunut sisään. Klikkaa alla olevaa nappia liittyäksesi organisaatioon.
              </Typography>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleJoinAsExistingUser}
                disabled={loading}
              >
                {loading ? 'Liitytään...' : `Liity organisaatioon ${organizationInfo?.name}`}
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" align="center">
                Väärä tili?{' '}
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  color="primary"
                  onClick={() => navigate('/auth')}
                >
                  Kirjaudu toisella tilillä
                </Link>
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // ------------------------------------------------------------------
  // Not logged in → register / login tabs
  // ------------------------------------------------------------------
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', pt: 4, pb: 6 }}>
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 52, height: 52, borderRadius: '14px', bgcolor: 'primary.main',
              boxShadow: isDark ? '0 0 0 8px rgba(34,211,238,0.08)' : '0 0 0 8px rgba(8,145,178,0.08)',
              mb: 2.5,
            }}
          >
            <UserPlus size={26} color="#fff" />
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">Liity organisaatioon</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Luo tili tai kirjaudu sisään liittyäksesi
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <OrgBanner />

            <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); }} sx={{ mb: 3 }}>
              <Tab icon={<UserPlus size={16} />} iconPosition="start" label="Uusi käyttäjä" />
              <Tab icon={<LogIn size={16} />} iconPosition="start" label="Minulla on tili" />
            </Tabs>

            {tab === 0 && (
              <RegisterForm onSubmit={handleRegister} loading={loading} error={error} />
            )}
            {tab === 1 && (
              <LoginForm onSubmit={handleLogin} loading={loading} error={error} />
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default JoinOrganization;